import axios, { AxiosError, type AxiosInstance, type InternalAxiosRequestConfig } from "axios";

/** Базовый фабричный метод: ставим baseURL и withCredentials */
export function createAxiosWithBaseUrl(
    baseURL: string,
    opts?: { withCredentials?: boolean },
): AxiosInstance {
    return axios.create({
        baseURL,
        withCredentials: opts?.withCredentials ?? true,
        headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
        },
    });
}

/** Навесить Authorization: Bearer <access>, если он есть. Вернёт функцию-eject чтобы снять перехватчик. */
export function applyJwtAuth(api: AxiosInstance, getAccessToken: () => string | null): () => void {
    const id = api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
        // можно пометить конкретный запрос как "без авторизации": config.headers["X-Skip-Auth"] = "1"
        if (!config.headers?.["X-Skip-Auth"]) {
            const at = getAccessToken();
            if (at) config.headers.Authorization = `Bearer ${at}`;
        }
        return config;
    });
    return () => api.interceptors.request.eject(id);
}

/**
 * Перехват 401 → вызвать refresh() ровно ОДИН раз и повторить все «подвисшие» запросы с новым access.
 * refresh() ДОЛЖЕН вернуть НОВЫЙ access или null.
 */
export function configureRefreshRetry(
    api: AxiosInstance,
    refresh: () => Promise<string | null>,
    opts?: {
        isRefreshRequest?: (cfg: InternalAxiosRequestConfig) => boolean; // по умолчанию ловим /api/auth/refresh
        onUnauthorizedFinal?: () => void; // что делать, когда refresh не помог (например, логаут)
    },
): () => void {
    let refreshPromise: Promise<string | null> | null = null;
    const waiters: Array<(t: string | null) => void> = [];
    const isRefresh =
        opts?.isRefreshRequest ?? ((cfg) => (cfg.url || "").endsWith("/api/auth/refresh"));

    const id = api.interceptors.response.use(
        (r) => r,
        async (error: AxiosError) => {
            const original = error.config as InternalAxiosRequestConfig & {
                _retry?: boolean;
            };
            const status = error.response?.status;

            // не пытаемся рефрешить сам refresh-запрос или если уже ретраили
            if (status !== 401 || original?._retry || (original && isRefresh(original))) {
                return Promise.reject(error);
            }

            original._retry = true;

            // один общий refresh для всех «одновременных» 401
            if (!refreshPromise) {
                refreshPromise = refresh()
                    .catch(() => null)
                    .finally(() => {
                        const p = refreshPromise;
                        refreshPromise = null;
                        // раздаём результат всем ожидающим
                        p?.then((token) => waiters.splice(0).forEach((w) => w(token)));
                    });
            }

            // ждём результата refresh
            const newToken = await new Promise<string | null>((resolve) => waiters.push(resolve));

            if (newToken) {
                // повторяем оригинальный запрос с новым Authorization
                original.headers.Authorization = `Bearer ${newToken}`;

                return api.request(original);
            }

            // refresh не выдал токен → финальная неавторизованность
            opts?.onUnauthorizedFinal?.();
            return Promise.reject(error);
        },
    );

    return () => api.interceptors.response.eject(id);
}
